import type { TApiFieldError, TResolvedApiError } from './api-error.types'

type THttpError = Error & {
  name: 'HTTPError'
  response: {
    status: number
  }
  data?: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isHttpError = (error: unknown): error is THttpError =>
  isRecord(error) &&
  error.name === 'HTTPError' &&
  isRecord(error.response) &&
  typeof error.response.status === 'number'

const getString = (value: unknown): string | null =>
  typeof value === 'string' ? value.trim() || null : null

const getResponseMessage = (data: unknown): string | null =>
  isRecord(data) ? getString(data.message) : null

const getFieldErrors = (data: unknown): TApiFieldError[] | null => {
  if (!isRecord(data) || !Array.isArray(data.errors) || data.errors.length === 0) {
    return null
  }

  const fieldErrors: TApiFieldError[] = []
  const usedPointers = new Set<string>()

  const hasOnlyValidFieldErrors = data.errors.every((error) => {
    if (!isRecord(error) || !isRecord(error.source)) return false

    const pointer = getString(error.source.pointer)
    const message = getString(error.title) ?? getString(error.detail)

    if (!pointer || !message) return false

    if (!usedPointers.has(pointer)) {
      usedPointers.add(pointer)
      fieldErrors.push({ pointer, message })
    }

    return true
  })

  return hasOnlyValidFieldErrors ? fieldErrors : null
}

export const resolveApiError = (error: unknown, fallback: string): TResolvedApiError => {
  if (isHttpError(error)) {
    const { status } = error.response

    if (status >= 400 && status < 500) {
      const summary = getResponseMessage(error.data) ?? fallback
      const fieldErrors = getFieldErrors(error.data)

      if (fieldErrors) {
        return {
          type: 'field-errors',
          summary,
          errors: fieldErrors,
        }
      }

      return { type: 'toast', message: summary }
    }

    return { type: 'toast', message: fallback }
  }

  if (error instanceof Error && error.name === 'Error') {
    return { type: 'toast', message: error.message.trim() || fallback }
  }

  return { type: 'toast', message: fallback }
}
